/**
 * Timeline Component
 * 
 * Displays work order events in chronological order with different styling
 * for different event types and an empty state when no events are available.
 */

import React, { useState, useMemo } from 'react';
import { css } from '@emotion/css';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Outline, { OutlineLink } from '@amzn/meridian/outline';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { WorkOrderEvent, JobType } from '../../../services/equipment/types';
import { TimelineEmptyState } from './timeline-empty-state';

export interface TimelineProps {
  /** Array of work order events to display */
  events: WorkOrderEvent[];
  /** The ID of the equipment to show events for */
  equipmentId: string;
  /** Site identifier */
  site?: string;
  /** Number of events to initially display */
  limit?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: string;
  /** Callback for loading more events */
  onLoadMore?: () => void;
  /** Whether there are more events to load */
  hasMore?: boolean;
  /** Whether to show filters */
  showFilters?: boolean;
  /** Current date range being used for filtering (for display purposes) */
  dateRange?: { startDate: string; endDate: string } | null;
  /** Whether date range filtering is active */
  isDateRangeFiltered?: boolean;
  /** Whether no equipment is selected */
  noEquipmentSelected?: boolean;
}

/**
 * Formats a date string for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString('en-GB');
  }
};

const getWorkOrderDisplayName = (workOrder: WorkOrderEvent): string => {
  return `${formatDate(workOrder.createdDate)}: ${workOrder.id} (${workOrder.jobType}): ${workOrder.description}`;
}

/**
 * Timeline component that displays work order events chronologically
 */
export const Timeline: React.FC<TimelineProps> = ({
  events,
  equipmentId,
  site,
  limit,
  isLoading = false,
  error,
  onLoadMore,
  hasMore = false,
  showFilters = true,
  dateRange: globalDateRange,
  isDateRangeFiltered = false,
  noEquipmentSelected = false
}) => {

  // Filter states (date range is handled globally via context)
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for tracking copied work order ID
  const [copiedWorkOrderId, setCopiedWorkOrderId] = useState<string | null>(null);

  // Sort and filter events
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = [...events];

    // Filter by job type
    if (selectedJobTypes.length > 0) {
      filtered = filtered.filter(event => selectedJobTypes.includes(event.jobType));
    }

    // Note: Date range filtering is handled by the service layer and context

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event =>
        event.description.toLowerCase().includes(term) ||
        event.id.toLowerCase().includes(term) ||
        event.jobType.toLowerCase().includes(term)
      );
    }

    // Sort chronologically with most recent first
    return filtered.sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
  }, [events, selectedJobTypes, searchTerm]);

  // Apply limit if specified
  const displayedEvents = useMemo(() => {
    return limit ? filteredAndSortedEvents.slice(0, limit) : filteredAndSortedEvents;
  }, [filteredAndSortedEvents, limit]);

  // Clear all local filters (date range is handled globally via context)
  const handleClearFilters = () => {
    setSelectedJobTypes([]);
    setSearchTerm('');
  };

  // Handle work order click to copy ID to clipboard
  const handleWorkOrderClick = async (event: React.MouseEvent, workOrderId: string) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    try {
      await navigator.clipboard.writeText(workOrderId);
      setCopiedWorkOrderId(workOrderId);
      // Clear the toast after 2 seconds
      setTimeout(() => setCopiedWorkOrderId(null), 2000);
    } catch (error) {
      console.error('Failed to copy work order ID:', error);
      // Fallback for older browsers or when clipboard API fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = workOrderId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedWorkOrderId(workOrderId);
        setTimeout(() => setCopiedWorkOrderId(null), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy method also failed:', fallbackError);
      }
    }
  };

  return (
    <Card>
      <Column spacing="300" spacingInset="400">
        <Row>
          <Heading level={5}>Maintenance History (Work Orders)</Heading>
          {events.length > 0 && (
            <Text>
              {displayedEvents.length}/{filteredAndSortedEvents.length}
              {filteredAndSortedEvents.length !== events.length && " (filtered)"}
            </Text>
          )}
        </Row>

        {/* Toast notification when work order ID is copied */}
        {copiedWorkOrderId && (
          <Alert type="success" size="small">
            Work Order ID "{copiedWorkOrderId}" copied to clipboard
          </Alert>
        )}

        {error && (
          <Alert size="medium" type="error">
            Failed to load events: {error}
          </Alert>
        )}

        {noEquipmentSelected && (
          <Column alignmentHorizontal="center" spacing="200">
            <Alert>
              Select an equipment node from the tree to view maintenance history.
            </Alert>
          </Column>
        )}

        {!error && !noEquipmentSelected && (
          <>
            {isLoading && events.length === 0 && (
              <Column alignmentHorizontal="center" spacing="200">
                <Loader size="medium" />
                <Text>Loading events...</Text>
              </Column>
            )}

            {!isLoading && events.length === 0 && (
              <Column alignmentHorizontal="start" spacing="200">
                <Text>No timeline data available for this equipment.</Text>
              </Column>
            )}

            {events.length > 0 && displayedEvents.length === 0 && filteredAndSortedEvents.length === 0 && (
              <Column alignmentHorizontal="center" spacing="200">
                <Text>No events match the current filters.</Text>
                <Button onClick={handleClearFilters}>Clear Filters</Button>
              </Column>
            )}

            {displayedEvents.length > 0 && (
              <Column spacing="200" alignmentHorizontal='start'>
                  <Outline forceOpen={true} linkWidth="100%">
                    {displayedEvents.map((event) => (
                      <OutlineLink 
                        key={event.id}
                        selected={true}
                        onClick={(e) => handleWorkOrderClick(e, event.id)}
                      >
                        {getWorkOrderDisplayName(event)}
                      </OutlineLink>
                    ))}
                  </Outline>

              </Column>
            )}

            {hasMore && onLoadMore && (
              <Column alignmentHorizontal="center">
                <Button onClick={onLoadMore} disabled={isLoading}>
                  {isLoading ? 'Loading...' : 'Load More Events'}
                </Button>
              </Column>
            )}
          </>
        )}
      </Column>
    </Card>
  );
};
